import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  Palette,
  Loader2,
  ScanLine,
  Play,
  CheckCircle,
  AlertCircle,
  SprayCan,
  Droplets,
} from 'lucide-react';

interface WorkOrder {
  id: string;
  order_number: string;
  product_name: string;
  customer: string;
  quantity: number;
  priority: string;
  status: string;
  workflow_type: string;
  paint_booth_id: string | null;
  requires_inserts: boolean;
  parent_work_order_id: string | null;
  created_at: string;
}

interface PaintBooth {
  id: string;
  code: string;
  name: string;
  nfc_uid: string | null;
  capacity: number;
  status: 'available' | 'in_use' | 'maintenance';
  current_work_order_id: string | null;
  started_at: string | null;
  pieces_count?: number;
}

interface Shelf {
  id: string;
  code: string;
  name: string;
}

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-800',
  normal: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
};

const BOOTH_STATUS_COLORS: Record<string, string> = {
  available: 'bg-green-100 text-green-800',
  in_use: 'bg-blue-100 text-blue-800',
  maintenance: 'bg-gray-100 text-gray-800',
};

export default function PaintingStation() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [booths, setBooths] = useState<PaintBooth[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [nfcInput, setNfcInput] = useState('');

  // Diálogos
  const [assignBoothDialogOpen, setAssignBoothDialogOpen] = useState(false);
  const [selectedWO, setSelectedWO] = useState<WorkOrder | null>(null);
  const [selectedBoothId, setSelectedBoothId] = useState('');

  const [closeBoothDialogOpen, setCloseBoothDialogOpen] = useState(false);
  const [selectedBooth, setSelectedBooth] = useState<PaintBooth | null>(null);

  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Cargar OTs de pintura
      const { data: woData, error: woError } = await api
        .from('work_orders')
        .select('*')
        .eq('workflow_type', 'painting')
        .in('status', ['pending', 'in_progress'])
        .order('priority', { ascending: false })
        .order('created_at');

      if (woError) throw woError;

      // Cargar cabinas con conteo de piezas
      const { data: boothsData, error: boothsError } = await api
        .from('paint_booths')
        .select('*')
        .eq('is_active', true)
        .order('code');

      if (boothsError) throw boothsError;

      // Contar piezas por cabina
      const { data: piecesCount } = await api
        .from('paint_booth_pieces')
        .select('paint_booth_id')
        .is('removed_at', null);

      const countMap: Record<string, number> = {};
      (piecesCount || []).forEach((p: any) => {
        countMap[p.paint_booth_id] = (countMap[p.paint_booth_id] || 0) + 1;
      });

      const boothsWithCount = (boothsData || []).map((b: PaintBooth) => ({
        ...b,
        pieces_count: countMap[b.id] || 0,
      }));

      // Cargar estanterías
      const { data: shelvesData } = await api
        .from('workshop_shelves')
        .select('*')
        .eq('is_active', true);

      setWorkOrders(woData || []);
      setBooths(boothsWithCount);
      setShelves(shelvesData || []);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Error cargando datos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleNfcScan = async () => {
    if (!nfcInput.trim()) return;

    // Buscar si es una cabina
    const booth = booths.find(b => b.nfc_uid === nfcInput.trim());
    if (booth) {
      if (booth.status === 'in_use') {
        setSelectedBooth(booth);
        setCloseBoothDialogOpen(true);
      } else {
        toast({
          title: 'Cabina disponible',
          description: `${booth.name} está disponible para usar`,
        });
      }
      setNfcInput('');
      return;
    }

    toast({
      title: 'No encontrado',
      description: `NFC ${nfcInput} no corresponde a ninguna cabina`,
      variant: 'destructive',
    });
    setNfcInput('');
  };

  const handleAssignBooth = async () => {
    if (!selectedWO || !selectedBoothId) return;

    setSaving(true);
    try {
      const booth = booths.find(b => b.id === selectedBoothId);

      // 1. Actualizar la cabina
      const { error: boothError } = await api
        .from('paint_booths')
        .update({
          status: 'in_use',
          current_work_order_id: selectedWO.id,
          started_at: new Date().toISOString(),
        })
        .eq('id', selectedBoothId);

      if (boothError) throw boothError;

      // 2. Actualizar la OT
      const { error: woError } = await api
        .from('work_orders')
        .update({
          status: 'in_progress',
          paint_booth_id: selectedBoothId,
          assigned_to: user?.id,
        })
        .eq('id', selectedWO.id);

      if (woError) throw woError;

      // 3. Registrar pieza en cabina (simulado - en real vendría de work_order_pieces)
      const { error: pieceError } = await api
        .from('paint_booth_pieces')
        .insert({
          paint_booth_id: selectedBoothId,
          work_order_piece_id: selectedWO.id, // Temporal
          work_order_id: selectedWO.id,
          status: 'painting',
        });

      // Ignorar error si falla por FK

      toast({
        title: 'Pieza en cabina',
        description: `OT ${selectedWO.order_number} asignada a ${booth?.name}`,
      });

      setAssignBoothDialogOpen(false);
      setSelectedWO(null);
      setSelectedBoothId('');
      loadData();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseBooth = async () => {
    if (!selectedBooth) return;

    setSaving(true);
    try {
      // 1. Obtener todas las piezas de la cabina
      const { data: boothPieces } = await api
        .from('paint_booth_pieces')
        .select('*, work_order:work_orders(*)')
        .eq('paint_booth_id', selectedBooth.id)
        .is('removed_at', null);

      // 2. Marcar piezas como removidas
      const { error: removePiecesError } = await api
        .from('paint_booth_pieces')
        .update({
          removed_at: new Date().toISOString(),
          status: 'removed',
        })
        .eq('paint_booth_id', selectedBooth.id)
        .is('removed_at', null);

      if (removePiecesError) throw removePiecesError;

      // 3. Procesar cada OT
      for (const piece of boothPieces || []) {
        const wo = piece.work_order as WorkOrder;
        if (!wo) continue;

        // Marcar OT de pintura como completada
        await api
          .from('work_orders')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString(),
          })
          .eq('id', wo.id);

        // Si requiere insertos, crear OT de ensamblaje final
        if (wo.requires_inserts) {
          const shelfComp = shelves.find(s => s.code === 'EST-COMP');
          await api
            .from('work_orders')
            .insert({
              order_number: `${wo.order_number.replace('/PNT', '')}/ASM-F`,
              product_name: wo.product_name,
              customer: wo.customer,
              quantity: wo.quantity,
              priority: wo.priority,
              status: 'pending',
              workflow_type: 'assembly',
              source_shelf_id: shelfComp?.id,
              parent_work_order_id: wo.parent_work_order_id,
              requires_inserts: true,
            });
        }
      }

      // 4. Liberar cabina
      const { error: boothError } = await api
        .from('paint_booths')
        .update({
          status: 'available',
          current_work_order_id: null,
          started_at: null,
        })
        .eq('id', selectedBooth.id);

      if (boothError) throw boothError;

      const piecesCount = boothPieces?.length || 0;
      toast({
        title: 'Cabina cerrada',
        description: `${piecesCount} pieza(s) procesada(s). Mover a destino correspondiente.`,
      });

      setCloseBoothDialogOpen(false);
      setSelectedBooth(null);
      loadData();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const pendingWOs = workOrders.filter(wo => wo.status === 'pending');
  const inProgressWOs = workOrders.filter(wo => wo.status === 'in_progress');
  const availableBooths = booths.filter(b => b.status === 'available');
  const activeBooths = booths.filter(b => b.status === 'in_use');

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Palette className="h-6 w-6" />
              Estación de Pintura
            </h1>
            <p className="text-muted-foreground">
              Pintura y barnizado de piezas
            </p>
          </div>
        </div>

        {/* Escáner NFC */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5" />
              Escanear Cabina
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <input
                type="text"
                placeholder="Escanea el NFC de la cabina para cerrarla..."
                value={nfcInput}
                onChange={(e) => setNfcInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleNfcScan()}
                className="flex-1 px-3 py-2 border rounded-md"
              />
              <Button onClick={handleNfcScan}>
                Buscar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Estado de cabinas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {booths.map((booth) => (
            <Card
              key={booth.id}
              className={booth.status === 'in_use' ? 'border-blue-500' : ''}
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-base">
                  <span className="flex items-center gap-2">
                    <SprayCan className="h-4 w-4" />
                    {booth.code}
                  </span>
                  <Badge className={BOOTH_STATUS_COLORS[booth.status]}>
                    {booth.status === 'available' ? 'Libre' :
                     booth.status === 'in_use' ? 'En uso' : 'Mant.'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{booth.name}</p>
                {booth.status === 'in_use' && (
                  <div className="mt-2 space-y-1">
                    <p className="text-sm">
                      <Droplets className="h-3 w-3 inline mr-1" />
                      {booth.pieces_count} pieza(s)
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setSelectedBooth(booth);
                        setCloseBoothDialogOpen(true);
                      }}
                    >
                      Cerrar cabina
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending">
              OTs Pendientes ({pendingWOs.length})
            </TabsTrigger>
            <TabsTrigger value="in_progress">
              En Cabina ({inProgressWOs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : pendingWOs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No hay OTs de pintura pendientes</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>OT</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Prioridad</TableHead>
                        <TableHead className="text-right">Acción</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingWOs.map((wo) => (
                        <TableRow key={wo.id}>
                          <TableCell>
                            <Badge variant="outline">{wo.order_number}</Badge>
                          </TableCell>
                          <TableCell>{wo.product_name}</TableCell>
                          <TableCell>{wo.customer}</TableCell>
                          <TableCell>
                            <Badge className={PRIORITY_COLORS[wo.priority]}>
                              {wo.priority}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedWO(wo);
                                setAssignBoothDialogOpen(true);
                              }}
                              disabled={availableBooths.length === 0}
                            >
                              <SprayCan className="h-4 w-4 mr-1" />
                              Asignar Cabina
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="in_progress">
            <Card>
              <CardContent className="pt-6">
                {inProgressWOs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <SprayCan className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No hay piezas en las cabinas</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>OT</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead>Cabina</TableHead>
                        <TableHead>Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inProgressWOs.map((wo) => {
                        const booth = booths.find(b => b.id === wo.paint_booth_id);
                        return (
                          <TableRow key={wo.id}>
                            <TableCell>
                              <Badge variant="outline">{wo.order_number}</Badge>
                            </TableCell>
                            <TableCell>{wo.product_name}</TableCell>
                            <TableCell>
                              {booth ? (
                                <Badge variant="secondary">{booth.code}</Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-blue-100 text-blue-800">
                                <Droplets className="h-3 w-3 mr-1" />
                                Pintando
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Diálogo asignar cabina */}
      <Dialog open={assignBoothDialogOpen} onOpenChange={setAssignBoothDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignar a Cabina de Pintura</DialogTitle>
            <DialogDescription>
              OT {selectedWO?.order_number} - {selectedWO?.product_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Seleccionar Cabina</Label>
              <Select value={selectedBoothId} onValueChange={setSelectedBoothId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una cabina..." />
                </SelectTrigger>
                <SelectContent>
                  {availableBooths.map((booth) => (
                    <SelectItem key={booth.id} value={booth.id}>
                      {booth.code} - {booth.name} (cap. {booth.capacity})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {availableBooths.length === 0 && (
              <p className="text-sm text-orange-600">
                No hay cabinas disponibles. Espera a que se libere una.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignBoothDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAssignBooth}
              disabled={saving || !selectedBoothId}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Asignar y Pintar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo cerrar cabina */}
      <AlertDialog open={closeBoothDialogOpen} onOpenChange={setCloseBoothDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cerrar Cabina {selectedBooth?.code}</AlertDialogTitle>
            <AlertDialogDescription>
              Todas las piezas de esta cabina ({selectedBooth?.pieces_count || 0}) serán marcadas
              como pintadas y movidas a su siguiente destino:
              <ul className="mt-2 list-disc list-inside text-sm">
                <li>Si requieren insertos → EST-COMP (Ensamblaje final)</li>
                <li>Si no → EST-TERM (Terminadas)</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleCloseBooth} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Cerrar Cabina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
